import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { columnConfigService } from '../../services/api/columnConfig'
import { 
  InvoiceColumnConfiguration,
  CreateColumnConfigRequest,
  UpdateColumnConfigRequest,
  ColumnConfig,
  DEFAULT_INVOICE_COLUMNS,
  PaginationParams
} from '../../types/api'
import { isApiError } from '../../utils/api'

interface ColumnConfigState {
  configurations: InvoiceColumnConfiguration[]
  activeConfiguration: InvoiceColumnConfiguration | null
  defaultConfiguration: InvoiceColumnConfiguration | null
  currentConfiguration: InvoiceColumnConfiguration | null
  templates: InvoiceColumnConfiguration[]
  
  pagination: {
    page: number
    per_page: number
    total: number
    last_page: number
  }
  
  loading: boolean
  creating: boolean
  updating: boolean
  deleting: boolean
  activating: boolean
  templatesLoading: boolean
  error: string | null
  
  // UI state for configuration editor
  editorState: {
    isOpen: boolean
    mode: 'create' | 'edit' | 'view'
    selectedCategory: 'header' | 'line_item' | 'payment' | 'address' | 'zatca'
    isDirty: boolean
    previewMode: boolean
  }
  
  // Temporary state for unsaved changes
  draftConfiguration: InvoiceColumnConfiguration | null
}

const initialState: ColumnConfigState = {
  configurations: [],
  activeConfiguration: null,
  defaultConfiguration: null,
  currentConfiguration: null,
  templates: [],
  
  pagination: {
    page: 1,
    per_page: 10,
    total: 0,
    last_page: 1
  },
  
  loading: false,
  creating: false,
  updating: false,
  deleting: false,
  activating: false,
  templatesLoading: false,
  error: null,
  
  editorState: {
    isOpen: false,
    mode: 'view',
    selectedCategory: 'header',
    isDirty: false,
    previewMode: false
  },
  
  draftConfiguration: null
}

// Async thunks
export const fetchColumnConfigurations = createAsyncThunk(
  'columnConfig/fetchConfigurations',
  async (params: { pagination?: PaginationParams } = {}, { rejectWithValue }) => {
    try {
      const response = await columnConfigService.getColumnConfigurations(params.pagination)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to fetch column configurations')
    }
  }
)

export const fetchActiveConfiguration = createAsyncThunk(
  'columnConfig/fetchActive',
  async (_, { rejectWithValue }) => {
    try {
      const response = await columnConfigService.getActiveColumnConfiguration()
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to fetch active configuration')
    }
  }
)

export const fetchDefaultConfiguration = createAsyncThunk(
  'columnConfig/fetchDefault',
  async (_, { rejectWithValue }) => {
    try {
      const response = await columnConfigService.getDefaultColumnConfiguration()
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to fetch default configuration')
    }
  }
)

export const createColumnConfiguration = createAsyncThunk(
  'columnConfig/create',
  async (data: CreateColumnConfigRequest, { rejectWithValue }) => {
    try {
      const response = await columnConfigService.createColumnConfiguration(data)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to create column configuration')
    }
  }
)

export const updateColumnConfiguration = createAsyncThunk(
  'columnConfig/update',
  async ({ id, data }: { id: string; data: UpdateColumnConfigRequest }, { rejectWithValue }) => {
    try {
      const response = await columnConfigService.updateColumnConfiguration(id, data)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to update column configuration')
    }
  }
)

export const deleteColumnConfiguration = createAsyncThunk(
  'columnConfig/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      await columnConfigService.deleteColumnConfiguration(id)
      return id
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to delete column configuration')
    }
  }
)

export const setActiveConfiguration = createAsyncThunk(
  'columnConfig/setActive',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await columnConfigService.setActiveConfiguration(id)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to set active configuration')
    }
  }
)

export const duplicateConfiguration = createAsyncThunk(
  'columnConfig/duplicate',
  async ({ id, name }: { id: string; name: string }, { rejectWithValue }) => {
    try {
      const response = await columnConfigService.duplicateConfiguration(id, name)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to duplicate configuration')
    }
  }
)

export const resetToDefault = createAsyncThunk(
  'columnConfig/resetToDefault',
  async (_, { rejectWithValue }) => {
    try {
      const response = await columnConfigService.resetToDefault()
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to reset to default configuration')
    }
  }
)

export const fetchConfigurationTemplates = createAsyncThunk(
  'columnConfig/fetchTemplates',
  async (_, { rejectWithValue }) => {
    try {
      const response = await columnConfigService.getConfigurationTemplates()
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to fetch configuration templates')
    }
  }
)

// Create slice
const columnConfigSlice = createSlice({
  name: 'columnConfig',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    
    // Editor state management
    openEditor: (state, action: PayloadAction<{ mode: 'create' | 'edit' | 'view'; configuration?: InvoiceColumnConfiguration }>) => {
      state.editorState.isOpen = true
      state.editorState.mode = action.payload.mode
      state.editorState.isDirty = false
      state.editorState.previewMode = false
      
      if (action.payload.configuration) {
        state.currentConfiguration = action.payload.configuration
        state.draftConfiguration = JSON.parse(JSON.stringify(action.payload.configuration))
      } else if (action.payload.mode === 'create') {
        // Create new configuration with default columns
        state.draftConfiguration = {
          id: '',
          name: '',
          description: '',
          is_default: false,
          is_active: false,
          created_at: '',
          updated_at: '',
          header_columns: DEFAULT_INVOICE_COLUMNS.header,
          line_item_columns: DEFAULT_INVOICE_COLUMNS.line_items,
          payment_columns: DEFAULT_INVOICE_COLUMNS.payments,
          address_columns: DEFAULT_INVOICE_COLUMNS.address,
          zatca_columns: DEFAULT_INVOICE_COLUMNS.zatca
        }
      }
    },
    
    closeEditor: (state) => {
      state.editorState.isOpen = false
      state.editorState.isDirty = false
      state.editorState.previewMode = false
      state.currentConfiguration = null
      state.draftConfiguration = null
    },
    
    setSelectedCategory: (state, action: PayloadAction<'header' | 'line_item' | 'payment' | 'address' | 'zatca'>) => {
      state.editorState.selectedCategory = action.payload
    },
    
    setPreviewMode: (state, action: PayloadAction<boolean>) => {
      state.editorState.previewMode = action.payload
    },
    
    // Draft configuration management
    updateDraftConfiguration: (state, action: PayloadAction<Partial<InvoiceColumnConfiguration>>) => {
      if (state.draftConfiguration) {
        state.draftConfiguration = { ...state.draftConfiguration, ...action.payload }
        state.editorState.isDirty = true
      }
    },
    
    updateDraftColumns: (state, action: PayloadAction<{ 
      category: 'header' | 'line_item' | 'payment' | 'address' | 'zatca'
      columns: ColumnConfig[] 
    }>) => {
      if (state.draftConfiguration) {
        const categoryKey = `${action.payload.category}_columns` as keyof InvoiceColumnConfiguration
        ;(state.draftConfiguration as any)[categoryKey] = action.payload.columns
        state.editorState.isDirty = true
      }
    },
    
    toggleColumnVisibility: (state, action: PayloadAction<{
      category: 'header' | 'line_item' | 'payment' | 'address' | 'zatca'
      columnKey: string
    }>) => {
      if (state.draftConfiguration) {
        const categoryKey = `${action.payload.category}_columns` as keyof InvoiceColumnConfiguration
        const columns = (state.draftConfiguration as any)[categoryKey] as ColumnConfig[]
        const column = columns.find(col => col.key === action.payload.columnKey)
        if (column) {
          column.visible = !column.visible
          state.editorState.isDirty = true
        }
      }
    },
    
    reorderColumns: (state, action: PayloadAction<{
      category: 'header' | 'line_item' | 'payment' | 'address' | 'zatca'
      fromIndex: number
      toIndex: number
    }>) => {
      if (state.draftConfiguration) {
        const categoryKey = `${action.payload.category}_columns` as keyof InvoiceColumnConfiguration
        const columns = (state.draftConfiguration as any)[categoryKey] as ColumnConfig[]
        const [movedColumn] = columns.splice(action.payload.fromIndex, 1)
        columns.splice(action.payload.toIndex, 0, movedColumn)
        
        // Update order values
        columns.forEach((col, index) => {
          col.order = index + 1
        })
        
        state.editorState.isDirty = true
      }
    },
    
    resetDraftToDefault: (state, action: PayloadAction<'header' | 'line_item' | 'payment' | 'address' | 'zatca'>) => {
      if (state.draftConfiguration) {
        const category = action.payload
        const categoryKey = `${category}_columns` as keyof InvoiceColumnConfiguration
        const defaultColumns = DEFAULT_INVOICE_COLUMNS[category === 'line_item' ? 'line_items' : category]
        ;(state.draftConfiguration as any)[categoryKey] = JSON.parse(JSON.stringify(defaultColumns))
        state.editorState.isDirty = true
      }
    },
    
    setPagination: (state, action: PayloadAction<Partial<typeof initialState.pagination>>) => {
      state.pagination = { ...state.pagination, ...action.payload }
    }
  },
  
  extraReducers: (builder) => {
    // Fetch configurations
    builder
      .addCase(fetchColumnConfigurations.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchColumnConfigurations.fulfilled, (state, action) => {
        state.loading = false
        state.configurations = action.payload.data
        state.pagination = {
          page: action.payload.page,
          per_page: action.payload.per_page,
          total: action.payload.total,
          last_page: action.payload.last_page
        }
      })
      .addCase(fetchColumnConfigurations.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Fetch active configuration
    builder
      .addCase(fetchActiveConfiguration.fulfilled, (state, action) => {
        state.activeConfiguration = action.payload
      })
      .addCase(fetchActiveConfiguration.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Fetch default configuration
    builder
      .addCase(fetchDefaultConfiguration.fulfilled, (state, action) => {
        state.defaultConfiguration = action.payload
      })
      .addCase(fetchDefaultConfiguration.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Create configuration
    builder
      .addCase(createColumnConfiguration.pending, (state) => {
        state.creating = true
        state.error = null
      })
      .addCase(createColumnConfiguration.fulfilled, (state, action) => {
        state.creating = false
        state.configurations.unshift(action.payload)
        state.editorState.isOpen = false
        state.editorState.isDirty = false
        state.draftConfiguration = null
      })
      .addCase(createColumnConfiguration.rejected, (state, action) => {
        state.creating = false
        state.error = action.payload as string
      })

    // Update configuration
    builder
      .addCase(updateColumnConfiguration.pending, (state) => {
        state.updating = true
        state.error = null
      })
      .addCase(updateColumnConfiguration.fulfilled, (state, action) => {
        state.updating = false
        const index = state.configurations.findIndex(config => config.id === action.payload.id)
        if (index !== -1) {
          state.configurations[index] = action.payload
        }
        if (state.activeConfiguration?.id === action.payload.id) {
          state.activeConfiguration = action.payload
        }
        state.editorState.isOpen = false
        state.editorState.isDirty = false
        state.draftConfiguration = null
      })
      .addCase(updateColumnConfiguration.rejected, (state, action) => {
        state.updating = false
        state.error = action.payload as string
      })

    // Delete configuration
    builder
      .addCase(deleteColumnConfiguration.pending, (state) => {
        state.deleting = true
        state.error = null
      })
      .addCase(deleteColumnConfiguration.fulfilled, (state, action) => {
        state.deleting = false
        state.configurations = state.configurations.filter(config => config.id !== action.payload)
        if (state.activeConfiguration?.id === action.payload) {
          state.activeConfiguration = null
        }
      })
      .addCase(deleteColumnConfiguration.rejected, (state, action) => {
        state.deleting = false
        state.error = action.payload as string
      })

    // Set active configuration
    builder
      .addCase(setActiveConfiguration.pending, (state) => {
        state.activating = true
        state.error = null
      })
      .addCase(setActiveConfiguration.fulfilled, (state, action) => {
        state.activating = false
        state.activeConfiguration = action.payload
        // Update is_active flag in configurations list
        state.configurations.forEach(config => {
          config.is_active = config.id === action.payload.id
        })
      })
      .addCase(setActiveConfiguration.rejected, (state, action) => {
        state.activating = false
        state.error = action.payload as string
      })

    // Duplicate configuration
    builder
      .addCase(duplicateConfiguration.fulfilled, (state, action) => {
        state.configurations.unshift(action.payload)
      })
      .addCase(duplicateConfiguration.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Reset to default
    builder
      .addCase(resetToDefault.fulfilled, (state, action) => {
        state.activeConfiguration = action.payload
        // Update configurations list
        const index = state.configurations.findIndex(config => config.id === action.payload.id)
        if (index !== -1) {
          state.configurations[index] = action.payload
        }
      })
      .addCase(resetToDefault.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Fetch templates
    builder
      .addCase(fetchConfigurationTemplates.pending, (state) => {
        state.templatesLoading = true
      })
      .addCase(fetchConfigurationTemplates.fulfilled, (state, action) => {
        state.templatesLoading = false
        state.templates = action.payload
      })
      .addCase(fetchConfigurationTemplates.rejected, (state, action) => {
        state.templatesLoading = false
        state.error = action.payload as string
      })
  }
})

// Export actions
export const {
  clearError,
  openEditor,
  closeEditor,
  setSelectedCategory,
  setPreviewMode,
  updateDraftConfiguration,
  updateDraftColumns,
  toggleColumnVisibility,
  reorderColumns,
  resetDraftToDefault,
  setPagination
} = columnConfigSlice.actions

// Export selectors
export const selectColumnConfigurations = (state: { columnConfig: ColumnConfigState }) => state.columnConfig.configurations
export const selectActiveConfiguration = (state: { columnConfig: ColumnConfigState }) => state.columnConfig.activeConfiguration
export const selectDefaultConfiguration = (state: { columnConfig: ColumnConfigState }) => state.columnConfig.defaultConfiguration
export const selectCurrentConfiguration = (state: { columnConfig: ColumnConfigState }) => state.columnConfig.currentConfiguration
export const selectDraftConfiguration = (state: { columnConfig: ColumnConfigState }) => state.columnConfig.draftConfiguration
export const selectConfigurationTemplates = (state: { columnConfig: ColumnConfigState }) => state.columnConfig.templates
export const selectColumnConfigLoading = (state: { columnConfig: ColumnConfigState }) => state.columnConfig.loading
export const selectColumnConfigError = (state: { columnConfig: ColumnConfigState }) => state.columnConfig.error
export const selectEditorState = (state: { columnConfig: ColumnConfigState }) => state.columnConfig.editorState
export const selectColumnConfigPagination = (state: { columnConfig: ColumnConfigState }) => state.columnConfig.pagination

// Export reducer
export default columnConfigSlice.reducer 